import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Circle, Download, KeyRound, LogOut, Plus, Wallet } from 'lucide-react';
import {
  depositDeductionKinds,
  formatCents,
  formatDate,
  noticeEndDate,
  parseEuroToCents,
  todayIso,
  type DepositDeductionKind,
  type Lease,
  type MoveOutCheck,
  type TerminationBy,
} from '@vuokramani/shared';
import { DetailList, Section, pageStyles as s } from '../../components/page.tsx';
import {
  Alert,
  Button,
  ChoiceGroup,
  Dialog,
  DialogActions,
  Notice,
  Panel,
  SelectField,
  TextArea,
  TextField,
} from '../../components/ui.tsx';
import { errorKey } from '../../lib/api.ts';
import { photoUrl, useInspections } from '../../lib/inspections.ts';
import {
  depositPdfUrl,
  useCancelTermination,
  useCloseLease,
  useTerminateLease,
} from '../../lib/tenancy.ts';
import ts from '../shared/tenancy.module.css';

/**
 * Vuokrasuhteen päättyminen vuokranantajalle. Ennen irtisanomista painike; sen jälkeen
 * vaiheet: lopputarkastus, avaimet ja vakuuden palautus, joka päättää vuokrasuhteen.
 */
export function LeaseEnding({ lease, previous = false }: { lease: Lease; previous?: boolean }) {
  const { t, i18n } = useTranslation();
  const [dialog, setDialog] = useState<'terminate' | 'close' | null>(null);
  const cancel = useCancelTermination(lease.propertyId, lease.id);
  const { data: inspections } = useInspections(lease.terminatedBy ? lease.id : undefined);
  const moveOut = inspections?.find((i) => i.type === 'move_out');

  if (!lease.terminatedBy) {
    return (
      <Section title={t('ending.title')}>
        <Panel>
          <div className={s.vacantRow}>
            <span style={{ color: 'var(--muted)' }}>{t('ending.intro')}</span>
            <Button onClick={() => setDialog('terminate')}>
              <LogOut size={16} aria-hidden="true" />
              {t('ending.terminate')}
            </Button>
          </div>
        </Panel>
        {dialog === 'terminate' && (
          <TerminateDialog lease={lease} onClose={() => setDialog(null)} />
        )}
      </Section>
    );
  }

  const inspectionState = !moveOut
    ? 'notStarted'
    : moveOut.status === 'approved'
      ? 'approved'
      : moveOut.status === 'submitted'
        ? 'submitted'
        : 'draft';
  const end = formatDate(lease.endDate!, i18n.language);
  // Päättymispäivä ohi: vuokralainen on muuttanut pois, irtisanomista ei voi enää perua.
  const past = lease.endDate! < todayIso();
  const tenants = lease.parties
    .filter((p) => p.role === 'tenant')
    .map((p) => p.name)
    .join(', ');

  return (
    <Section
      title={previous ? t('ending.previousTitle', { tenants }) : t('ending.title')}
      note={previous ? t('ending.previousNote') : undefined}
    >
      <Panel>
        <p className={ts.endingHead}>
          <strong>{t(past ? 'ending.endedOn' : 'ending.endsOn', { date: end })}</strong>
          <span>
            {t(`ending.by.${lease.terminatedBy}`)}
            {lease.noticeOn ? `, ${formatDate(lease.noticeOn, i18n.language)}` : ''}
          </span>
        </p>
        <ol className={ts.steps}>
          <li className={inspectionState === 'approved' ? ts.stepDone : ''}>
            {inspectionState === 'approved' ? (
              <CheckCircle2 size={18} aria-hidden="true" />
            ) : (
              <Circle size={18} aria-hidden="true" />
            )}
            <span>
              <strong>{t('ending.step1')}</strong>
              <span className={ts.fileMeta}>{t(`ending.inspection.${inspectionState}`)}</span>
            </span>
            <Link to="../tarkastus" relative="path" className={ts.stepLink}>
              {t('ending.open')}
            </Link>
          </li>
          <li>
            <KeyRound size={18} aria-hidden="true" />
            <span>
              <strong>{t('ending.step2')}</strong>
              <span className={ts.fileMeta}>{t('ending.keysText')}</span>
            </span>
          </li>
          <li>
            <Wallet size={18} aria-hidden="true" />
            <span>
              <strong>{t('ending.step3')}</strong>
              <span className={ts.fileMeta}>
                {t('ending.depositText', {
                  amount: formatCents(lease.depositCents, i18n.language),
                })}
              </span>
            </span>
            <Button variant="primary" onClick={() => setDialog('close')}>
              {t('ending.close')}
            </Button>
          </li>
        </ol>
        {!previous && !past && (
          <Button
            variant="ghost"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate()}
            style={{ marginTop: '0.5rem' }}
          >
            {t('ending.cancel')}
          </Button>
        )}
      </Panel>
      {dialog === 'close' && <CloseDialog lease={lease} onClose={() => setDialog(null)} />}
    </Section>
  );
}

const BY_OPTIONS: TerminationBy[] = ['tenant', 'landlord', 'mutual', 'fixed_term'];

function TerminateDialog({ lease, onClose }: { lease: Lease; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const terminate = useTerminateLease(lease.propertyId, lease.id);
  const [by, setBy] = useState<TerminationBy>('tenant');
  const [noticeOn, setNoticeOn] = useState(todayIso());
  const suggested = (b: TerminationBy, n: string) =>
    b === 'fixed_term' && lease.endDate ? lease.endDate : noticeEndDate(b, n, lease.startDate);
  const [endDate, setEndDate] = useState(suggested('tenant', todayIso()));
  const [edited, setEdited] = useState(false);

  function change(nextBy: TerminationBy, nextNotice: string) {
    setBy(nextBy);
    setNoticeOn(nextNotice);
    if (!edited && /^\d{4}-\d{2}-\d{2}$/.test(nextNotice)) {
      setEndDate(suggested(nextBy, nextNotice));
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    terminate.mutate({ by, noticeOn, endDate }, { onSuccess: onClose });
  }

  const hasRecipients = lease.parties.some((p) => p.access !== 'none');
  const options = BY_OPTIONS.filter((o) => o !== 'fixed_term' || lease.endDate);

  return (
    <Dialog open onClose={onClose} title={t('ending.terminate')}>
      <form onSubmit={submit} noValidate style={{ display: 'grid', gap: '1.1rem' }}>
        <SelectField
          label={t('ending.who')}
          value={by}
          onChange={(e) => change(e.target.value as TerminationBy, noticeOn)}
        >
          {options.map((o) => (
            <option key={o} value={o}>
              {t(`ending.whoOption.${o}`)}
            </option>
          ))}
        </SelectField>
        <TextField
          label={t('ending.noticeOn')}
          type="date"
          value={noticeOn}
          onChange={(e) => change(by, e.target.value)}
        />
        <TextField
          label={t('ending.endDate')}
          hint={t(`ending.rule.${by}`)}
          type="date"
          value={endDate}
          onChange={(e) => {
            setEdited(true);
            setEndDate(e.target.value);
          }}
        />
        <Notice>
          {hasRecipients ? t('ending.willNotify') : t('ending.noNotify')}{' '}
          {t('ending.afterward', { date: endDate ? formatDate(endDate, i18n.language) : '–' })}
        </Notice>
        {terminate.isError && <Alert>{t(errorKey(terminate.error))}</Alert>}
        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={terminate.isPending || !endDate}>
            {t('ending.confirmTerminate')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

interface DeductionDraft {
  key: number;
  kind: DepositDeductionKind;
  amount: string;
  description: string;
  photoIds: string[];
}

/**
 * Vuokrasuhteen sulkeminen muistilistana: 1. lopputarkastus, 2. avoimet laskut,
 * 3. vakuus ja pidätykset (selite ja kuvat lopputarkastuksesta), 4. yhteenveto.
 * Palautus + pidätykset = vakuus. Maksamattoman vuokran pidätys kirjataan laskujen maksuksi.
 */
function CloseDialog({ lease, onClose }: { lease: Lease; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const money = (cents: number) => formatCents(cents, lang);
  const close = useCloseLease(lease.propertyId, lease.id);
  const { data: inspections } = useInspections(lease.id);
  const moveOut = inspections?.find((i) => i.type === 'move_out');
  const photos = moveOut?.rooms.flatMap((r) => r.photos.map((p) => ({ ...p, room: r.name }))) ?? [];

  // Ei valmiita valintoja: vuokranantaja vastaa itse tarkastukseen ja vakuuteen.
  const [check, setCheck] = useState<MoveOutCheck | null>(null);
  const [fullReturn, setFullReturn] = useState<boolean | null>(
    lease.depositCents === 0 ? true : null,
  );
  const [rows, setRows] = useState<DeductionDraft[]>([]);
  const [date, setDate] = useState(todayIso());
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const [nextKey, setNextKey] = useState(1);

  const parsed = rows.map((r) => ({ ...r, cents: parseEuroToCents(r.amount) }));
  const deducted = fullReturn ? 0 : parsed.reduce((n, r) => n + (r.cents ?? 0), 0);
  const returned = lease.depositCents - deducted;
  const rentDeducted = fullReturn
    ? 0
    : parsed.filter((r) => r.kind === 'unpaid_rent').reduce((n, r) => n + (r.cents ?? 0), 0);
  const hasDamage = !fullReturn && parsed.some((r) => r.kind === 'damage');

  const rowErrors = parsed.map((r) => ({
    amount: r.cents !== null && r.cents > 0 ? undefined : t('errors.amount'),
    description: r.description.trim() ? undefined : t('errors.required'),
  }));
  const formError =
    !moveOut && check === null
      ? t('deposit.chooseInspection')
      : fullReturn === null
        ? t('deposit.chooseDeposit')
        : !fullReturn
          ? rows.length === 0
            ? t('deposit.noRows')
            : returned < 0
              ? t('deposit.overDeposit', { amount: money(lease.depositCents) })
              : rentDeducted > lease.openInvoicesCents
                ? t('deposit.overOpen', { amount: money(lease.openInvoicesCents) })
                : undefined
          : undefined;
  const invalid =
    Boolean(formError) ||
    !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
    (!fullReturn && rowErrors.some((e) => e.amount || e.description));

  function update(key: number, patch: Partial<DeductionDraft>) {
    setRows((list) => list.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }
  function addRow() {
    setRows((list) => [
      ...list,
      { key: nextKey, kind: 'damage', amount: '', description: '', photoIds: [] },
    ]);
    setNextKey(nextKey + 1);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    setTried(true);
    if (invalid) return;
    close.mutate(
      {
        depositReturnedCents: returned,
        depositReturnedOn: date,
        note: note.trim() || undefined,
        moveOutCheck: moveOut ? 'app' : check!,
        deductions: fullReturn
          ? []
          : parsed.map((r) => ({
              kind: r.kind,
              amountCents: r.cents!,
              description: r.description.trim(),
              photoIds: r.photoIds,
            })),
      },
      { onSuccess: onClose },
    );
  }

  const inspectionText = !moveOut
    ? null
    : moveOut.status === 'approved'
      ? t('ending.inspection.approved')
      : moveOut.status === 'submitted'
        ? t('ending.inspection.submitted')
        : t('ending.inspection.draft');

  return (
    <Dialog open onClose={onClose} title={t('deposit.title')} wide>
      <form onSubmit={submit} noValidate className={ts.closeForm}>
        {/* 1. Lopputarkastus */}
        <section className={ts.closeStep}>
          <h3>{t('deposit.step1')}</h3>
          {moveOut ? (
            <p className={ts.fileMeta}>
              {inspectionText} {t('deposit.inspectionPhotosHint')}
            </p>
          ) : (
            <>
              <p className={ts.fileMeta}>{t('deposit.noInspection')}</p>
              <ChoiceGroup<MoveOutCheck>
                name="move-out-check"
                legend={t('deposit.inspectionDone')}
                value={check}
                onChange={setCheck}
                options={[
                  { value: 'external', title: t('deposit.check.external') },
                  { value: 'none', title: t('deposit.check.none') },
                ]}
              />
            </>
          )}
        </section>

        {/* 2. Avoimet laskut */}
        <section className={ts.closeStep}>
          <h3>{t('deposit.step2')}</h3>
          <p className={ts.fileMeta}>
            {lease.openInvoicesCents > 0
              ? t('deposit.openInvoices', { amount: money(lease.openInvoicesCents) })
              : t('deposit.noOpenInvoices')}
          </p>
        </section>

        {/* 3. Vakuus */}
        <section className={ts.closeStep}>
          <h3>{t('deposit.step3', { amount: money(lease.depositCents) })}</h3>
          {lease.depositCents === 0 ? (
            <p className={ts.fileMeta}>{t('deposit.noDeposit')}</p>
          ) : (
            <>
              {lease.openInvoicesCents > 0 && (
                <Notice>
                  {t('deposit.unpaidWarning', { amount: money(lease.openInvoicesCents) })}
                </Notice>
              )}
              <ChoiceGroup<'full' | 'partial'>
                name="deposit-full"
                legend={t('deposit.fullQuestion')}
                value={fullReturn === null ? null : fullReturn ? 'full' : 'partial'}
                onChange={(v) => {
                  setFullReturn(v === 'full');
                  if (v === 'partial' && rows.length === 0) addRow();
                }}
                options={[
                  { value: 'full', title: t('deposit.full'), text: t('deposit.fullText') },
                  {
                    value: 'partial',
                    title: t('deposit.partial'),
                    text: t('deposit.partialText'),
                  },
                ]}
              />
            </>
          )}
          {fullReturn === false && (
            <>
              <p className={ts.fileMeta}>{t('deposit.justify')}</p>
              {parsed.map((r, i) => (
                <div key={r.key} className={ts.deduction}>
                  <div className={s.formRow}>
                    <SelectField
                      label={t('deposit.kindLabel')}
                      value={r.kind}
                      onChange={(e) =>
                        update(r.key, { kind: e.target.value as DepositDeductionKind })
                      }
                    >
                      {depositDeductionKinds.map((k) => (
                        <option key={k} value={k}>
                          {t(`deposit.kind.${k}`)}
                        </option>
                      ))}
                    </SelectField>
                    <TextField
                      label={t('deposit.amount')}
                      inputMode="decimal"
                      value={r.amount}
                      onChange={(e) => update(r.key, { amount: e.target.value })}
                      error={tried ? rowErrors[i]!.amount : undefined}
                    />
                  </div>
                  <TextArea
                    label={t('deposit.description')}
                    hint={t(`deposit.descriptionHint.${r.kind}`)}
                    rows={2}
                    value={r.description}
                    onChange={(e) => update(r.key, { description: e.target.value })}
                    error={tried ? rowErrors[i]!.description : undefined}
                  />
                  {photos.length > 0 && r.kind !== 'unpaid_rent' && (
                    <fieldset className={ts.photoPick}>
                      <legend>{t('deposit.photos')}</legend>
                      {photos.map((p) => {
                        const selected = r.photoIds.includes(p.id);
                        return (
                          <label key={p.id} className={selected ? ts.photoSelected : undefined}>
                            <input
                              type="checkbox"
                              className="visually-hidden"
                              checked={selected}
                              onChange={() =>
                                update(r.key, {
                                  photoIds: selected
                                    ? r.photoIds.filter((x) => x !== p.id)
                                    : [...r.photoIds, p.id],
                                })
                              }
                            />
                            <img src={photoUrl(p.id)} alt={p.room} loading="lazy" />
                            <span>{p.room}</span>
                          </label>
                        );
                      })}
                    </fieldset>
                  )}
                  <div>
                    <Button
                      variant="ghost"
                      onClick={() => setRows((list) => list.filter((x) => x.key !== r.key))}
                    >
                      {t('deposit.removeRow')}
                    </Button>
                  </div>
                </div>
              ))}
              <div>
                <Button onClick={addRow}>
                  <Plus size={16} aria-hidden="true" />
                  {t('deposit.addRow')}
                </Button>
              </div>
            </>
          )}
          <div className={s.formRow}>
            <TextField
              label={t('ending.returnedOn')}
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <TextArea
            label={t('deposit.note')}
            hint={t('deposit.noteHint')}
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </section>

        {/* 4. Yhteenveto */}
        <section className={ts.closeStep}>
          <h3>{t('deposit.step4')}</h3>
          <p>
            <strong>
              {t('deposit.summary', {
                returned: money(Math.max(returned, 0)),
                deducted: money(deducted),
              })}
            </strong>
          </p>
          <Notice>
            {t('deposit.what')}{' '}
            {lease.parties.some((p) => p.access === 'joined')
              ? t('deposit.tenantSees')
              : t('deposit.tenantPdf')}
          </Notice>
          {hasDamage && <Notice>{t('deposit.taxTip')}</Notice>}
          {rentDeducted > 0 && <Notice>{t('deposit.rentTip')}</Notice>}
        </section>

        {tried && formError && <Alert>{formError}</Alert>}
        {close.isError && <Alert>{t(errorKey(close.error))}</Alert>}
        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={close.isPending}>
            {t('ending.confirmClose')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/** Päättyneet vuokrasuhteet vapaan asunnon Vuokrasuhde-välilehdellä. */
export function EndedLeases({ leases }: { leases: Lease[] }) {
  const { t, i18n } = useTranslation();
  const ended = leases.filter((l) => l.status === 'ended');
  if (ended.length === 0) return null;
  return (
    <Section title={t('ending.history')}>
      <Panel>
        <DetailList
          rows={ended.map((l) => ({
            label: `${formatDate(l.startDate, i18n.language)} – ${
              l.endDate ? formatDate(l.endDate, i18n.language) : ''
            }`,
            value: (
              <span style={{ display: 'grid', gap: 2 }}>
                <span>
                  {l.parties
                    .filter((p) => p.role === 'tenant')
                    .map((p) => p.name)
                    .join(', ')}
                </span>
                <span className={ts.fileMeta}>
                  {t('lease.perMonth', { amount: formatCents(l.rentCents, i18n.language) })}
                  {l.depositReturnedCents !== null &&
                    ` · ${t('ending.depositReturned', {
                      amount: formatCents(l.depositReturnedCents, i18n.language),
                    })}`}
                  {l.depositNote && ` (${l.depositNote})`}
                </span>
                {l.depositDeductions.length > 0 && (
                  <span className={ts.fileMeta}>
                    {l.depositDeductions
                      .map(
                        (d) =>
                          `${t(`deposit.kind.${d.kind}`)} ${formatCents(d.amountCents, i18n.language)}`,
                      )
                      .join(' · ')}
                  </span>
                )}
                {l.depositReturnedOn && (
                  <a href={depositPdfUrl(l.id)} download className={ts.stepLink}>
                    <Download size={14} aria-hidden="true" /> {t('deposit.pdf')}
                  </a>
                )}
              </span>
            ),
          }))}
        />
      </Panel>
    </Section>
  );
}
